import { loadStripe } from '@stripe/stripe-js';
import { useState } from 'react';
import { calculatePaymentFees } from '~/utils/calculatePaymentFees';
import { useShoppingCart } from '~/utils/context/useShoppingCart';
import fetchWrapper from '~/utils/fetchWrapper';
import setSentryError from '~/utils/setSentryError';
import { PaymentFeesItem } from '~/utils/types/shoppingCart';
import Card from '../Card';
import validateAddress from '~/utils/validateAddress';
import { isFetchWrapperError } from '~/utils/errorCheckers';

const stripePromise = loadStripe('pk_live_51Igo17HIvoER3tBQEK5UzfoSqZT0iWsvKk04NPyU1V98ZcsAjAA27Wi1DUv1IEwzKg8aGwWxbCp0cVlWbpzHZsgi00eStUR94w');
// const stripePromise = loadStripe('pk_test_51Igo17HIvoER3tBQIR7XmUQeGxLoFhpftSafeHcT0oDPl3mFbETgkNGQeb35GuT6M1gywrkU4Eo6QnigRCEWFH6U009tgOHlA5');

export default function CheckoutButton({ currency, setParcelError, subButtonMessage, buttonMessage }: { currency: string, setParcelError: React.Dispatch<React.SetStateAction<{ message: string } | null>>, subButtonMessage: string, buttonMessage: string }) {
    const { cartItems, orderTotal, validateOrderObject, cartHasShippingItems, cartHasCardItems } = useShoppingCart();
    const [hasPaymentError, setHasPaymentError] = useState<boolean>(false)
    const [showAddressChangeDetected, setShowAddressChangeDetected] = useState<boolean>(false);
    const [updatedAddress, setUpdatedAddress] = useState<any>(null);

    const handleClick = async (e: any) => {
        if (e) e.preventDefault();
        const form = document.getElementById('orderForm') as HTMLFormElement;
        const formData: FormData = new FormData(form || undefined);
        const paymentFees = calculatePaymentFees(cartItems);

        if (cartItems.some(item => item.type === 'paymentFees')) {
            let paymentFeesIndex = cartItems.findIndex(item => item.type === 'paymentFees');
            cartItems[paymentFeesIndex].price = paymentFees;
        } else {
            const paymentFeesItem: PaymentFeesItem = {
                id: 'paymentFees',
                title: 'Payment Fees',
                price: paymentFees,
                quantity: 1,
                type: 'paymentFees'
            }
            cartItems.push(paymentFeesItem);
        }

        console.log({ paymentFees })

        let localOrderTotal = orderTotal;

        if (localOrderTotal !== (orderTotal + paymentFees)) {
            localOrderTotal = orderTotal + paymentFees;
        }

        const formDataObject: any = {
            orderLines: cartItems,
            currency: currency,
            currencyPrice: (localOrderTotal).toFixed(2),
            // Refactor addressStart to address because addressStart is used to prevent autofill
            address: formData.get('addressStart') || '',
            parcelID: '',
            ...Object.fromEntries(formData.entries()),
            state: formData.get('state') || formData.get('stateAbbr') || '',
            orderNumber: 0
        }

        const addressValidation = await validateAddress(formDataObject);

        if (isFetchWrapperError(addressValidation)) {
            setHasPaymentError(true);
            return;
        }

        if (addressValidation.hasChanged) {
            setShowAddressChangeDetected(true);
            setUpdatedAddress(addressValidation);
            return;
        }

        const orderIsValid = validateOrderObject(formDataObject);
        if (!orderIsValid) {
            return;
        }

        try {
            const parcelCreated: any = await fetchWrapper('/api/createParcel', {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ order: formDataObject })
            });

            console.log({ parcelCreated })

            if (!parcelCreated || !parcelCreated.parcelID) {
                setHasPaymentError(true);
                setParcelError({ message: parcelCreated.error?.message });
                return;
            }

            const parcelID = parcelCreated.parcelID;
            formDataObject.parcelID = parcelID;
            formDataObject.orderNumber = parseInt(parcelCreated.orderNumber)

        } catch (e: any) {
            console.log(e);
            setSentryError('Error in CheckoutButton', e)
        }

        let checkoutSession: any = null;
        // Call your backend to create the Checkout Session
        try {
            checkoutSession = await fetchWrapper('/api/createCheckoutSession', {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formDataObject)
            });

            if (!checkoutSession) {
                setHasPaymentError(true);
                return;
            }

        } catch (e: any) {
            console.log(e);
            setSentryError('Error in CheckoutButton', e)
        }

        const stripe = await stripePromise;

        // Redirect to Stripe Checkout
        const result = await stripe?.redirectToCheckout({
            sessionId: checkoutSession.id,
        });

        if (result?.error) {
            // Display error.message in your UI.
            setHasPaymentError(true);
            console.error(result.error.message);
        }
    };


    function updateAddressAfterValidation() {
        updateFormValue('addressStart', updatedAddress.street);
        updateFormValue('city', updatedAddress.city);
        updateFormValue('stateAbbr', updatedAddress.stateAbbr);
        updateFormValue('state', updatedAddress.state);
        updateFormValue('postalCode', updatedAddress.postalCode.split('-')[0]);
        updateFormValue('countryAbbr', updatedAddress.country);

        handleClick(null);
    }

    function updateFormValue(id: string, value: string) {
        const form = document.getElementById('orderForm') as HTMLFormElement;
        const input = form.querySelector(`input[name="${id}"]`) as HTMLInputElement;
        if (input) {
            input.value = value;
        }
    }


    return (
        <div className="flex flex-col justify-center w-full">
            <button disabled={!cartHasShippingItems() || !cartHasCardItems()} className="flex gap-2 items-center justify-center w-full rounded-md border border-transparent bg-brand-blue disabled:bg-opacity-20 disabled:hover:bg-brand-blue disabled:hover:bg-opacity-20 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-brand-blueHover focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 focus:ring-offset-gray-50" onClick={handleClick}>
                {buttonMessage}
            </button>
            <small className="text-xs text-gray-500 mt-3 text-center opacity-60 font-bold">
                {subButtonMessage}
            </small>
            {hasPaymentError &&
                <p className="mb-6 mt-4 text-sm text-center text-red-500">
                    Something went wrong. Please refresh the page and try again.
                </p>
            }

            {showAddressChangeDetected &&
                <div className="fixed inset-0 flex items-center justify-center bg-black/20 z-20">
                    <Card>
                        <Card.Content>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">Address Change Detected</h3>
                            <p className="text-gray-700 mb-3">
                                We noticed a change in your shipping address during checkout validation. Please verify this is correct:
                            </p>
                            <div className="bg-amber-50 p-3 rounded-md border border-amber-200 mb-2">
                                <p className="font-medium text-gray-800">New Shipping Address:</p>
                                <p className="text-gray-700">
                                    {updatedAddress.street}<br />
                                    {updatedAddress.postalCode.split('-')[0]}, {updatedAddress.state}<br />
                                    {updatedAddress.city}
                                </p>
                            </div>
                            <p className="text-sm text-gray-500">
                                If this address is incorrect, click "Edit Address" to make changes.
                            </p>
                        </Card.Content>
                        <Card.Footer>
                            <div className="flex justify-end space-x-3">
                                <button onClick={() => setShowAddressChangeDetected(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                                    Edit Address
                                </button>
                                <button onClick={() => { setShowAddressChangeDetected(false); updateAddressAfterValidation() }} className="px-4 py-2 bg-brand-blue hover:bg-brand-blueHover focus:outline-none focus:ring-2 focus:ring-brand-blueHover focus:ring-offset-2 text-white rounded-md">
                                    Confirm Address
                                </button>
                            </div>
                        </Card.Footer>
                    </Card>
                </div>
            }
        </div>
    );
};
